import { format, isValid, parse } from 'date-fns';

// A value carries only a calendar date (no time-of-day) in these forms:
//   "YYYYMMDD" (DICOM DA), "YYYY-MM-DD" (ISO date), "YYYY-MM" (ISO year-month).
// NOTE: such values MUST be rendered as-is. Parsing them with `new Date()` treats
// them as UTC midnight, so `format()` shifts the day/month in non-UTC timezones
// (e.g. 27th shown as 26th in US Pacific) — see D7S-528.
const DATE_ONLY_FORMATS: ReadonlyArray<{ pattern: RegExp; fmt: string }> = [
  { pattern: /^\d{8}$/, fmt: 'yyyyMMdd' },
  { pattern: /^\d{4}-\d{2}-\d{2}$/, fmt: 'yyyy-MM-dd' },
  { pattern: /^\d{4}-\d{2}$/, fmt: 'yyyy-MM' },
];

/**
 * Parse a date-only string into a LOCAL Date so formatting applies no timezone
 * shift. Returns null when the value carries a time component or is not a
 * recognised date-only format — callers should then fall back to `new Date(value)`.
 */
export const parseDateOnly = (val: string): Date | null => {
  const trimmed = val.trim();
  for (const { pattern, fmt } of DATE_ONLY_FORMATS) {
    if (pattern.test(trimmed)) {
      const parsed = parse(trimmed, fmt, new Date());
      return isValid(parsed) ? parsed : null;
    }
  }
  return null;
};

export const d7Format = (val: Date | string, fmt: string) => {
  const date: Date = val instanceof Date ? val : (parseDateOnly(val) ?? new Date(val));
  return isValid(date) ? format(date, fmt) : '';
};

const UTC_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Parse a date-time string, treating a value without a timezone designator as
 * UTC: appending `Z` is valid only for designator-less strings; otherwise
 * falls back to native parsing.
 */
const parseDateTimeAsUtc = (val: string): Date => {
  const asUtc = new Date(`${val}Z`);
  return isValid(asUtc) ? asUtc : new Date(val);
};

/**
 * Format an instant as `dd-MMM-yyyy HH:mm:ss` in UTC, independent of the
 * browser timezone and DST. Offset-less strings are pinned to UTC — engines
 * would otherwise parse them as local time, shifting the result by the
 * browser's UTC offset. Returns '' for invalid input. Not for date-only
 * values (see `parseDateOnly`).
 */
export const formatUtcDateTime = (val: Date | string | number): string => {
  const date = typeof val === 'string' ? parseDateTimeAsUtc(val) : new Date(val);
  if (!isValid(date)) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${pad(date.getUTCDate())}-${UTC_MONTHS[date.getUTCMonth()]}-${date.getUTCFullYear()} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  );
};

export const shortDateTimeFormat = 'dd-LLL-yyyy HH:mm:ss';
export const shortDateTime = (val: Date | string) => d7Format(val, shortDateTimeFormat);

export const shortDateFormat = 'dd-LLL-yyyy';
export const shortDate = (val: Date | string) => d7Format(val, shortDateFormat);

export const timeSecFormat = 'HH:mm:ss';
export const timeSec = (val: Date | string) => d7Format(val, timeSecFormat);

export const shortDateUSFormat = 'yyyy-MM-dd';
export const shortDateUS = (val: Date | string) => d7Format(val, shortDateUSFormat);
